import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../config';
import SortableTable from './SortableTable';

const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const getStatsData = (payload) => Array.isArray(payload) ? payload : payload?.data || [];

const StatCard = ({ title, value, icon: Icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className="p-3 rounded-full bg-blue-100">
        <Icon className="h-6 w-6 text-blue-600" />
      </div>
      <div className="ml-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="text-2xl font-semibold text-blue-600 dark:text-blue-400">{formatNumber(value)}</p>
      </div>
    </div>
  </div>
);

// SVG Icons components
const UsersIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const PhotoIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const BuildingIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const DocumentIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SkeletonStatCard = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className="p-3 rounded-full bg-gray-200 animate-pulse">
        <div className="h-6 w-6" />
      </div>
      <div className="ml-4 flex-1">
        <div className="h-5 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
        <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
      </div>
    </div>
  </div>
);

export default function GlobalStatistics({ fetchWithAuth }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [projectStats, setProjectStats] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [sourceStats, setSourceStats] = useState(null);
  const [yearStats, setYearStats] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/statistics/general`);
        if (response.error) throw new Error(response.error);
        const data = await response.json();
        if (isMounted) setStats(data);
      } catch (error) {
        console.error('Error fetching statistics:', error);
      }
    };

    const fetchProjectStats = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/statistics/countByProject`);
        if (response.error) throw new Error(response.error);
        const data = await response.json();
        if (isMounted) setProjectStats(getStatsData(data));
      } catch (error) {
        console.error('Error fetching project statistics:', error);
      }
    };

    const fetchClientStats = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/statistics/countByClient`);
        if (response.error) throw new Error(response.error);
        const data = await response.json();
        if (isMounted) setClientStats(getStatsData(data));
      } catch (error) {
        console.error('Error fetching client statistics:', error);
      }
    };

    const fetchSourceStats = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/statistics/countBySource`);
        if (response.error) throw new Error(response.error);
        const data = await response.json();
        if (isMounted) setSourceStats(getStatsData(data));
      } catch (error) {
        console.error('Error fetching source statistics:', error);
      }
    };

    const fetchYearStats = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/statistics/countByYear`);
        if (response.error) throw new Error(response.error);
        const data = await response.json();
        if (isMounted) setYearStats(getStatsData(data));
      } catch (error) {
        console.error('Error fetching year statistics:', error);
      }
    };

    const fetchAllStats = async () => {
      await fetchStats();
      await fetchSourceStats();
      await fetchYearStats();
      await fetchClientStats();
      await fetchProjectStats();
    };

    fetchAllStats();

    return () => {
      isMounted = false;
    };
  }, [fetchWithAuth]);

  const statCards = [
    {
      title: t('Integrated clients'),
      value: stats?.clients || '--',
      icon: UsersIcon
    },
    {
      title: t('Projects'),
      value: stats?.projects || '--',
      icon: BuildingIcon
    },
    {
      title: t('Users'),
      value: stats?.users || '--',
      icon: UsersIcon
    },
    {
      title: t('Forms'),
      value: stats?.forms || '--',
      icon: DocumentIcon
    },
    {
      title: t('Photos'),
      value: stats?.pictures || '--',
      icon: PhotoIcon
    },
    {
      title: t('Shops'),
      value: stats?.shops || '--',
      icon: BuildingIcon
    },
    {
      title: t('Questions'),
      value: stats?.questions || '--',
      icon: DocumentIcon
    },
    {
      title: t('Items'),
      value: stats?.items || '--',
      icon: DocumentIcon
    }
  ];

  // Colonnes pour les tableaux
  const clientColumns = [
    { key: 'client', label: t('Client') },
    {
      key: 'pictures',
      label: t('Photos'),
      render: (item) => formatNumber(item.pictures)
    }
  ];

  const projectColumns = [
    { key: 'projectname', label: t('Project') },
    {
      key: 'pictures',
      label: t('Photos'),
      render: (item) => formatNumber(item.pictures)
    }
  ];

  const sourceColumns = [
    { key: 'source', label: t('Source') },
    {
      key: 'pictures',
      label: t('Photos'),
      render: (item) => formatNumber(item.pictures)
    }
  ];

  const yearColumns = [
    { key: 'year', label: t('Year') },
    {
      key: 'pictures',
      label: t('Photos'),
      render: (item) => formatNumber(item.pictures)
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{t('Global Statistics')}</h2>
        {stats?.fresh_since && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('Data from')} {dayjs(stats.fresh_since).format('L LT')}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {!stats ? (
          // Skeleton loading state
          [...Array(statCards.length)].map((_, index) => (
            <SkeletonStatCard key={index} />
          ))
        ) : (
          // Actual content
          statCards.map((card, index) => (
            <StatCard key={index} {...card} />
          ))
        )}
      </div>

      <div className="border-t border-gray-200 my-8"></div>

      {/* Première ligne: Source et Année */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-xl font-bold mb-4">{t('Photos by source')}</h3>
          {!sourceStats ? (
            <div className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <SortableTable
                data={sourceStats}
                columns={sourceColumns}
                emptyMessage={t('No source with photos')}
                initialSortBy={{ key: 'pictures', direction: 'descending' }}
                exportable={true}
                exportFilename="photos-par-source.csv"
              />
            </div>
          )}
        </div>

        <div>
          <h3 className="text-xl font-bold mb-4">{t('Photos by year')}</h3>
          {!yearStats ? (
            <div className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <SortableTable
                data={yearStats}
                columns={yearColumns}
                emptyMessage={t('No data by year')}
                initialSortBy={{ key: 'year', direction: 'descending' }}
                exportable={true}
                exportFilename="photos-par-annee.csv"
              />
            </div>
          )}
        </div>
      </div>

      {/* Deuxième ligne: Client et Projet */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xl font-bold mb-4">{t('Photos by client')}</h3>
          {!clientStats ? (
            <div className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <SortableTable
                data={clientStats}
                columns={clientColumns}
                emptyMessage={t('No client with photos')}
                initialSortBy={{ key: 'pictures', direction: 'descending' }}
                exportable={true}
                exportFilename="photos-par-client.csv"
              />
            </div>
          )}
        </div>

        <div>
          <h3 className="text-xl font-bold mb-4">{t('Photos by project')}</h3>
          {!projectStats ? (
            <div className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <SortableTable
                data={projectStats}
                columns={projectColumns}
                emptyMessage={t('No project with photos')}
                initialSortBy={{ key: 'pictures', direction: 'descending' }}
                exportable={true}
                exportFilename="photos-par-projet.csv"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
